import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Sparkles, Phone } from "lucide-react";
import { BRAND } from "@/config/brand";
import { INDUSTRIES } from "@/config/industries";
import PlugitAIChat from "@/pages/PlugitAIChat";

const C = BRAND.colors;

export default function IndustriesIndex() {
  useEffect(() => {
    document.title = "AI Websites & Sales Agents by Industry | PlugitAI";
    const set = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    set("description",
      "PlugitAI builds AI-powered websites and sales agents tuned to each industry — attorneys, auto shops, salons, and more. Every build ships with a $299.99 lock-in.");
    set("robots", "index,follow");

    const origin = process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://plugitai.com");
    let canon = document.querySelector("link[rel='canonical']");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = `${origin}/for`;
  }, []);

  return (
    <div className="min-h-screen w-full"
         style={{ background: C.ink, color: C.text, fontFamily: "'Inter', system-ui, sans-serif" }}
         data-testid="industries-index">
      <TopNav />

      <section className="relative z-10 pt-28 md:pt-40 pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <nav className="text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-8" style={{ color: C.textFade }}>
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <span style={{ color: C.text }}>Industries</span>
          </nav>

          <div className="max-w-[820px] mb-14">
            <div className="text-xs uppercase tracking-[0.22em] mb-3" style={{ color: C.greenGlow }}>
              PlugitAI · By industry
            </div>
            <h1 className="font-black tracking-[-0.02em] leading-[1.02]" style={{ fontSize: "clamp(2.2rem, 4.6vw, 4.4rem)" }}>
              AI websites tuned to{" "}
              <span style={{
                backgroundImage: `linear-gradient(135deg, ${C.blueGlow} 0%, ${C.greenGlow} 100%)`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>your business</span>.
            </h1>
            <p className="mt-4 text-lg md:text-xl leading-relaxed" style={{ color: C.textDim }}>
              Every industry has its own leaks — missed after-hours calls for auto shops, unqualified intake calls for attorneys, empty chairs for salons. We build the AI twin around the leak that costs your business the most.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {INDUSTRIES.map((ind) => (
              <Link key={ind.slug} to={`/for/${ind.slug}`}
                    className="group rounded-3xl p-6 md:p-8 border transition-all hover:-translate-y-1"
                    style={{
                      borderColor: `${ind.accent}44`,
                      background: `linear-gradient(160deg, ${ind.accent}14 0%, transparent 55%), rgba(17,24,39,0.62)`,
                      backdropFilter: "blur(10px)",
                      boxShadow: `0 40px 100px -40px ${ind.accent}55`,
                    }}
                    data-testid={`industry-card-${ind.slug}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                       style={{
                         background: `linear-gradient(135deg, ${ind.accent} 0%, ${ind.accentSoft} 100%)`,
                         boxShadow: `0 12px 32px -10px ${ind.accent}88`,
                       }}>
                    <Sparkles size={16} className="text-white" />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: ind.accentSoft }}>
                    AI for {ind.name}
                  </div>
                </div>
                <div className="font-black text-white text-xl md:text-2xl leading-tight mb-3">
                  {ind.display}
                </div>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: C.textDim }}>
                  {ind.tagline}
                </p>
                <div className="mt-6 flex flex-wrap gap-1.5">
                  {ind.serves.slice(0, 3).map((s) => (
                    <span key={s} className="text-[10px] px-2 py-0.5 rounded-full"
                          style={{ background: `${ind.accent}18`, color: ind.accentSoft, border: `1px solid ${ind.accent}44` }}>
                      {s}
                    </span>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-1 text-sm font-semibold"
                     style={{ color: ind.accentSoft }}>
                  Explore {ind.name} → <ArrowUpRight size={13} />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 rounded-3xl p-6 md:p-10 border"
               style={{
                 borderColor: C.line,
                 background: "rgba(17,24,39,0.55)",
                 backdropFilter: "blur(10px)",
               }}>
            <div className="text-xs uppercase tracking-[0.22em] mb-2" style={{ color: C.greenGlow }}>
              Don&apos;t see your industry?
            </div>
            <div className="font-black text-white text-xl md:text-2xl mb-2">
              We build for almost every small business.
            </div>
            <p className="text-sm md:text-base mb-5" style={{ color: C.textDim }}>
              Contractors, roofers, medical clinics, restaurants, real estate, tutors, gyms — if you have customers and a phone, we can build your AI twin. Call Kit directly and describe your business.
            </p>
            <a href="tel:+12148155966"
               className="inline-flex items-center gap-2 px-6 h-12 rounded-full font-semibold text-sm"
               style={{
                 background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`,
                 color: "white",
               }}>
              <Phone size={13} /> Call (214) 815-5966
            </a>
          </div>
        </div>
      </section>

      <PlugitAIChat />
    </div>
  );
}

function TopNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50"
            style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(14px)", borderBottom: `1px solid ${C.line}` }}>
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logos.plugitai} alt="PlugitAI" className="h-10 md:h-12 w-auto" />
          <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] pl-3 border-l gold-shine gold-shine-dark" style={{ borderColor: C.line }}>
            Powered by {BRAND.engineName}
          </span>
        </Link>
        <a href="tel:+12148155966"
           className="inline-flex items-center gap-2 px-4 md:px-5 h-10 md:h-11 rounded-full font-semibold text-sm"
           style={{ background: `linear-gradient(135deg, ${C.blueBright} 0%, ${C.greenBright} 100%)`, color: "white" }}>
          <Phone size={14} /> (214) 815-5966
        </a>
      </div>
    </header>
  );
}
